import { useState, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Row, Col, Modal, Spinner } from 'react-bootstrap'
import { motion, AnimatePresence } from 'framer-motion'
import { Formik, Form as FormikForm, Field } from 'formik'
import * as Yup from 'yup'
import {
  FaBuilding,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaEnvelope,
  FaPhone,
  FaFileInvoice,
  FaMapMarkerAlt,
  FaIdCard,
  FaSearch,
  FaWarehouse,
  FaCheckCircle,
  FaTimesCircle,
} from 'react-icons/fa'
import {
  fetchEntities,
  fetchEntity,
  addEntity,
  updateEntity,
  deleteEntity,
  clearSelectedEntity,
} from '../../store/slices/entitySlice'
import '../../assets/scss/pages/EntityManagement.scss'

const EntitySchema = Yup.object().shape({
  name: Yup.string().required('Entity name is required'),
  email: Yup.string().email('Invalid email').nullable(),
  phone: Yup.string().nullable(),
  panNumber: Yup.string().nullable(),
  gstNumber: Yup.string().nullable(),
  address: Yup.string().nullable(),
})

const fade = {
  hidden: { opacity: 0, y: 12 },
  visible: i => ({ opacity: 1, y: 0, transition: { delay: i * 0.05, duration: 0.32, ease: 'easeOut' } }),
}

const EntityManagement = () => {
  const dispatch = useDispatch()
  const { entities, selectedEntity, loading } = useSelector(state => state.entity)

  const [showModal, setShowModal]           = useState(false)
  const [editingEntity, setEditingEntity]   = useState(null)
  const [viewEntity, setViewEntity]         = useState(null)
  const [deleteModal, setDeleteModal]       = useState(false)
  const [entityToDelete, setEntityToDelete] = useState(null)
  const [statusFilter, setStatusFilter]     = useState('all')
  const [search, setSearch]                 = useState('')

  useEffect(() => { dispatch(fetchEntities()) }, [dispatch])

  useEffect(() => {
    if (selectedEntity) setViewEntity(selectedEntity)
  }, [selectedEntity])

  const initialValues = useMemo(() => ({
    name:      editingEntity?.name      || '',
    email:     editingEntity?.email     || '',
    phone:     editingEntity?.phone     || '',
    address:   editingEntity?.address   || '',
    panNumber: editingEntity?.panNumber || '',
    gstNumber: editingEntity?.gstNumber || '',
  }), [editingEntity])

  const handleCloseModal     = () => { setShowModal(false); setEditingEntity(null) }
  const handleShowViewModal  = (entity) => dispatch(fetchEntity(entity.id))
  const handleCloseViewModal = () => { setViewEntity(null); dispatch(clearSelectedEntity()) }

  const confirmDelete = async () => {
    if (entityToDelete) {
      await dispatch(deleteEntity(entityToDelete.id)).unwrap()
      setDeleteModal(false)
      setEntityToDelete(null)
    }
  }

  const filteredEntities = useMemo(() => {
    let list = entities
    if (statusFilter === 'active')   list = list.filter(e => e.isActive)
    if (statusFilter === 'inactive') list = list.filter(e => !e.isActive)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(e =>
        e.name?.toLowerCase().includes(q) ||
        e.email?.toLowerCase().includes(q) ||
        e.gstNumber?.toLowerCase().includes(q)
      )
    }
    return list
  }, [entities, statusFilter, search])

  const totalActive   = entities.filter(e => e.isActive).length
  const totalInactive = entities.filter(e => !e.isActive).length

  return (
    <div className="em-page">

      {/* HEADER */}
      <motion.div custom={0} variants={fade} initial="hidden" animate="visible" className="em-header">
        <div className="em-header-left">
          <div className="em-header-icon"><FaBuilding /></div>
          <div>
            <h1 className="em-title">Entity Management</h1>
            <p className="em-sub">Manage your registered business entities and branches</p>
          </div>
        </div>
        <button
          className="em-add-btn"
          onClick={() => { setEditingEntity(null); setShowModal(true) }}
        >
          <FaPlus /> Add Entity
        </button>
      </motion.div>

      {/* KPI SUMMARY */}
      <Row className="g-3 mb-3">
        {[
          { label: 'Total Entities', value: entities.length, note: 'registered',  icon: <FaWarehouse />,    color: 'blue'  },
          { label: 'Active',         value: totalActive,     note: 'operational', icon: <FaCheckCircle />,  color: 'green' },
          { label: 'Inactive',       value: totalInactive,   note: 'not active',  icon: <FaTimesCircle />,  color: 'red'   },
        ].map((k, i) => (
          <Col key={k.label} xs={12} md={4}>
            <motion.div custom={i + 1} variants={fade} initial="hidden" animate="visible" className={`em-kpi em-kpi--${k.color}`}>
              <div className="em-kpi-left">
                <div className="em-kpi-val">{k.value}</div>
                <div className="em-kpi-label">{k.label}</div>
                <div className="em-kpi-note">{k.note}</div>
              </div>
              <div className="em-kpi-icon">{k.icon}</div>
            </motion.div>
          </Col>
        ))}
      </Row>

      {/* FILTER BAR */}
      <motion.div custom={4} variants={fade} initial="hidden" animate="visible" className="em-filter-bar">
        <div className="em-search-wrap">
          <FaSearch className="em-search-icon" />
          <input
            className="em-search"
            placeholder="Search by name, email or GST…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="em-filter-tabs">
          {['all', 'active', 'inactive'].map(f => (
            <button
              key={f}
              className={`em-tab ${statusFilter === f ? 'em-tab--active' : ''}`}
              onClick={() => setStatusFilter(f)}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <span className="em-count">{filteredEntities.length} entities</span>
      </motion.div>

      {/* ENTITY LIST */}
      {loading ? (
        <div className="em-loading">
          <Spinner animation="border" style={{ color: 'rgb(20,35,115)' }} />
          <span>Loading entities…</span>
        </div>
      ) : filteredEntities.length === 0 ? (
        <motion.div custom={5} variants={fade} initial="hidden" animate="visible" className="em-empty">
          <FaWarehouse className="em-empty-icon" />
          <p className="em-empty-title">No entities found</p>
          <p className="em-empty-sub">Add your first entity or adjust the filter above.</p>
        </motion.div>
      ) : (
        <Row className="g-3">
          <AnimatePresence>
            {filteredEntities.map((entity, i) => (
              <Col key={entity.id} xs={12} sm={6} md={4} xl={3}>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ delay: i * 0.04, duration: 0.28 }}
                  className="em-card"
                >
                  {/* Card top bar accent */}
                  <div className="em-card-accent" />

                  {/* Header row */}
                  <div className="em-card-head">
                    <div className="em-card-avatar">
                      {entity.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="em-card-meta">
                      <div className="em-card-name">{entity.name}</div>
                      <span className={`em-badge ${entity.isActive ? 'em-badge--active' : 'em-badge--inactive'}`}>
                        {entity.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  {/* Detail rows */}
                  <div className="em-card-details">
                    <div className="em-card-row">
                      <FaEnvelope className="em-card-icon" />
                      <span className="em-card-val">{entity.email || '—'}</span>
                    </div>
                    <div className="em-card-row">
                      <FaPhone className="em-card-icon" />
                      <span className="em-card-val">{entity.phone || '—'}</span>
                    </div>
                    <div className="em-card-row">
                      <FaFileInvoice className="em-card-icon" />
                      <span className="em-card-val em-card-mono">{entity.gstNumber || '—'}</span>
                    </div>
                    {entity.address && (
                      <div className="em-card-row">
                        <FaMapMarkerAlt className="em-card-icon" />
                        <span className="em-card-val em-card-address">{entity.address}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="em-card-footer">
                    <button className="em-card-btn em-card-btn--view"   title="View"   onClick={() => handleShowViewModal(entity)}><FaEye /> View</button>
                    <button className="em-card-btn em-card-btn--edit"   title="Edit"   onClick={() => { setEditingEntity(entity); setShowModal(true) }}><FaEdit /> Edit</button>
                    <button className="em-card-btn em-card-btn--delete" title="Delete" onClick={() => { setEntityToDelete(entity); setDeleteModal(true) }}><FaTrash /></button>
                  </div>
                </motion.div>
              </Col>
            ))}
          </AnimatePresence>
        </Row>
      )}

      {/* ADD / EDIT MODAL */}
      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Formik
          initialValues={initialValues}
          validationSchema={EntitySchema}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            try {
              if (editingEntity) {
                await dispatch(updateEntity({ id: editingEntity.id, ...values })).unwrap()
              } else {
                await dispatch(addEntity(values)).unwrap()
              }
              handleCloseModal()
            } catch (err) {
              console.error('Failed to save entity:', err)
            } finally {
              setSubmitting(false)
            }
          }}
        >
          {({ errors, touched, isSubmitting }) => (
            <FormikForm>
              {/* ── Branded Modal Header ── */}
              <Modal.Header className="em-modal-hero" as="div">
                <button type="button" className="em-modal-close" onClick={handleCloseModal} aria-label="Close">✕</button>
                <div className="em-modal-hero-icon">
                  {editingEntity ? <FaEdit /> : <FaBuilding />}
                </div>
                <div className="em-modal-hero-text">
                  <h2 className="em-modal-hero-title">
                    {editingEntity ? 'Edit Entity' : 'Add New Entity'}
                  </h2>
                  <p className="em-modal-hero-sub">
                    {editingEntity
                      ? `Updating details for ${editingEntity.name}`
                      : 'Fill in the details below to register a new business entity'}
                  </p>
                </div>
              </Modal.Header>

              <Modal.Body className="em-modal-body">
                {/* ── Section: Basic Info ── */}
                <div className="em-form-section">
                  <Row className="g-3">
                    <Col md={12}>
                      <div className="em-form-group">
                        <label className="em-label">Entity Name <span className="em-required">*</span></label>
                        <Field
                          name="name"
                          className={`em-input ${touched.name && errors.name ? 'em-input--error' : ''}`}
                          placeholder="e.g. TradeSetu Logistics Pvt. Ltd."
                        />
                        {touched.name && errors.name && <span className="em-error">{errors.name}</span>}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="em-form-group">
                        <label className="em-label"><FaEnvelope className="em-lbl-icon" /> Email Address</label>
                        <Field
                          name="email" type="email"
                          className={`em-input ${touched.email && errors.email ? 'em-input--error' : ''}`}
                          placeholder="contact@entity.com"
                        />
                        {touched.email && errors.email && <span className="em-error">{errors.email}</span>}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="em-form-group">
                        <label className="em-label"><FaPhone className="em-lbl-icon" /> Phone Number</label>
                        <Field name="phone" className="em-input" placeholder="+91 00000 00000" />
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* ── Section: Tax & Compliance ── */}
                <div className="em-form-section">
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="em-form-group">
                        <label className="em-label"><FaIdCard className="em-lbl-icon" /> PAN Number</label>
                        <Field name="panNumber" className="em-input em-input--mono" placeholder="ABCDE1234F" />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="em-form-group">
                        <label className="em-label"><FaFileInvoice className="em-lbl-icon" /> GST Number</label>
                        <Field name="gstNumber" className="em-input em-input--mono" placeholder="22ABCDE1234F1Z5" />
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* ── Section: Location ── */}
                <div className="em-form-section em-form-section--last">
                  <Row className="g-3">
                    <Col md={12}>
                      <div className="em-form-group">
                        <label className="em-label"><FaMapMarkerAlt className="em-lbl-icon" /> Address</label>
                        <Field as="textarea" name="address" rows={3} className="em-input em-textarea" placeholder="Full address with city, state, pincode" />
                      </div>
                    </Col>
                  </Row>
                </div>
              </Modal.Body>

              {/* ── Footer ── */}
              <Modal.Footer className="em-modal-footer">
                <div className="em-modal-footer-note">
                  <span className="em-required">*</span> Required field
                </div>
                <div className="em-modal-footer-actions">
                  <button type="button" className="em-btn-cancel" onClick={handleCloseModal} disabled={isSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="em-btn-submit" disabled={isSubmitting}>
                    {isSubmitting
                      ? <><Spinner size="sm" animation="border" className="me-2" />Saving…</>
                      : <>{editingEntity ? <><FaEdit className="me-2" />Update Entity</> : <><FaPlus className="me-2" />Save Entity</>}</>}
                  </button>
                </div>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>

      {/* VIEW MODAL */}
      <Modal show={!!viewEntity} onHide={handleCloseViewModal} centered>
        <Modal.Header closeButton className="em-modal-header">
          <Modal.Title className="em-modal-title">Entity Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="em-modal-body">
          {viewEntity && (
            <div className="em-view-grid">
              <div className="em-view-avatar">
                {viewEntity.name?.charAt(0).toUpperCase()}
              </div>
              <div className="em-view-name">{viewEntity.name}</div>
              <span className={`em-badge ${viewEntity.isActive ? 'em-badge--active' : 'em-badge--inactive'}`}>
                {viewEntity.isActive ? 'Active' : 'Inactive'}
              </span>
              <div className="em-view-rows">
                {[
                  { icon: <FaEnvelope />,    label: 'Email',   val: viewEntity.email },
                  { icon: <FaPhone />,        label: 'Phone',   val: viewEntity.phone },
                  { icon: <FaIdCard />,       label: 'PAN',     val: viewEntity.panNumber },
                  { icon: <FaFileInvoice />,  label: 'GST',     val: viewEntity.gstNumber },
                  { icon: <FaMapMarkerAlt />, label: 'Address', val: viewEntity.address },
                ].map((row, i) => (
                  <div key={i} className="em-view-row">
                    <span className="em-view-icon">{row.icon}</span>
                    <div className="em-view-detail">
                      <span className="em-view-lbl">{row.label}</span>
                      <span className="em-view-val">{row.val || '—'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* DELETE CONFIRM MODAL */}
      <Modal show={deleteModal} onHide={() => setDeleteModal(false)} centered>
        <Modal.Header closeButton className="em-modal-header">
          <Modal.Title className="em-modal-title">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body className="em-modal-body em-delete-body">
          <div className="em-delete-icon"><FaTrash /></div>
          <p className="em-delete-msg">
            Are you sure you want to delete <strong>{entityToDelete?.name}</strong>?
            <br /><span className="em-delete-warn">This action cannot be undone.</span>
          </p>
          <div className="em-delete-actions">
            <button className="em-btn-cancel" onClick={() => setDeleteModal(false)}>Cancel</button>
            <button className="em-btn-delete" onClick={confirmDelete}>Delete</button>
          </div>
        </Modal.Body>
      </Modal>

    </div>
  )
}

export default EntityManagement
